import { ITEM_DEFS, itemLabel } from '../config/item-defs';
import { sellPrice } from '../sim/pricing';
import { amountMarkup } from './currency';
import { MONSTER_DISPLAY_NAME, MonsterArchetype } from '../config/monster-roster';
import { riteLapseLine } from './rite-copy';
import { t, tp, N_, getIntlLocale } from '../i18n/i18n';

/**
 * The run-end stage.
 *
 * User report (2026-08-24): *"when I die, right now it just shows 'You died'
 * across the whole screen, and that is it. After that, I cannot respawn,
 * return to the main menu, or do anything else. This stage needs to be
 * properly thought through."*
 *
 * Both halves of that were true and they had different causes.
 *
 * **Stuck.** The server closes the connection `DEATH_DISCONNECT_DELAY_MS`
 * (3s) after death, and the client used `onLeave` to bounce back to the hub.
 * On the direct-join dev path (`?view=dungeon_instance`) there is no hub to
 * bounce to and `onMatchEnd` is undefined, so the screen was genuinely
 * terminal. That is now impossible by construction: this overlay always
 * offers a way out, and if no handler was supplied it navigates to `/play`
 * itself rather than assuming someone else will.
 *
 * **Not thought through.** Even on the working path, a three-second flash of
 * two words is not a stage. An extraction game's death screen is where the
 * genre's whole proposition is settled up, and it has to answer four
 * questions in this order:
 *
 * 1. *What happened to me?* — the verdict, and what killed me. Cause of death
 *    is the most-asked question in the genre and only the server knew it.
 * 2. *What did it cost?* — the loot that is gone, named and priced. If the
 *    loss is not itemised, the risk the player was taking was never real to
 *    them, and the insurance system is invisible.
 * 3. *What did I keep anyway?* — insured loot and the XP a failed run still
 *    pays. Without this a death screen is a pure list of losses, which reads
 *    as punishment rather than as a hand played out.
 * 4. *What now?* — buttons. Never a timer.
 *
 * **It never dismisses itself.** The server may close the socket on its own
 * schedule; the player leaves this screen when they have finished reading it.
 * Decoupling those two is the actual fix — the old code conflated "the match
 * connection ended" with "the player is done".
 *
 * Plain DOM + Tailwind, like every other panel in `engine/ui/`.
 */

export interface RunEndItem {
  itemDefId: string;
  rarity: string;
  weightKg: number;
  value: number;
}

export type RunEndCause =
  | { kind: 'MONSTER'; ref: string }
  | { kind: 'PLAYER'; ref: string }
  | { kind: 'TIMEOUT' }
  | { kind: 'DISCONNECT' }
  /** The player used the run menu. Distinct from DISCONNECT so the sentence can be true. */
  | { kind: 'ABANDON' };

export interface RunEndInfo {
  outcome: 'EXTRACTED' | 'DIED';
  stratum: number;
  durationMs: number;
  cause?: RunEndCause | null;
  kept?: RunEndItem[];
  lost?: RunEndItem[];
  xpGained?: number;
  /**
   * The level this run's XP crossed into, when the *completion bonus* is what
   * crossed it (2026-08-25).
   *
   * A level reached mid-run has already had its pillar and its fanfare in the
   * world, and the room deliberately omits it here rather than replaying it —
   * so this is only ever set for a crossing nobody has celebrated yet, which
   * would otherwise be completely silent.
   */
  leveledTo?: number | null;
  insuranceSlots?: number;
  /**
   * Quick-use consumables (2026-08-25) — what the satchel still held when the
   * run ended, and whether it came home.
   *
   * Reported rather than left silent: dying with two Salves is 24 Wax of goods
   * gone, and a loss the screen does not mention is a loss that reads as a bug.
   */
  satchel?: { defId: string; charges: number }[];
  satchelReturned?: boolean;
  /**
   * design/gdd/rites.md — what the burned Rite paid, or why it paid nothing.
   *
   * Always sent when one was burned, including when `bonusXp` is 0. A Rite's
   * entire payload is a number the player would otherwise never see, so an
   * unreported one is an item nobody buys twice — and a lapse in particular
   * has to be said out loud, because a Deepwater Oath that paid nothing
   * because you surfaced from the Ossuary is a correct outcome that reads
   * exactly like a bug unless the screen names the floor it wanted.
   */
  rite?: {
    defId: string;
    name: string;
    bonusXp: number;
    lapsedReason: 'wrong_outcome' | 'wrong_floor' | null;
  } | null;
}

type CauseKind = 'EXTRACTED' | 'MONSTER' | 'PLAYER' | 'TIMEOUT' | 'DISCONNECT' | 'ABANDON' | 'DIED';

/**
 * One whole sentence per ending × floor (2026-10-16, i18n). The floor used to
 * be injected as "{where}", which a language with cases cannot decline — "в
 * {where}" needs a different form of the name than "из {where}". Index 0 is an
 * unknown floor, said by number. Monster sentences are phrased "Killed by
 * {monster}" so no language has to choose an article or a gender for the name.
 */
const CAUSE_SENTENCE: Record<CauseKind, readonly [string, string, string, string]> = {
  EXTRACTED: [N_('You climbed out of floor {n}.'), N_('You climbed out of the Cellars.'), N_('You climbed out of the Ossuary.'), N_('You climbed out of the Vaults.')],
  MONSTER: [N_('Killed by {monster} on floor {n}.'), N_('Killed by {monster} in the Cellars.'), N_('Killed by {monster} in the Ossuary.'), N_('Killed by {monster} in the Vaults.')],
  PLAYER: [N_('{player} killed you on floor {n}.'), N_('{player} killed you in the Cellars.'), N_('{player} killed you in the Ossuary.'), N_('{player} killed you in the Vaults.')],
  TIMEOUT: [N_('The dungeon sealed with you still inside, on floor {n}.'), N_('The dungeon sealed with you still inside the Cellars.'), N_('The dungeon sealed with you still inside the Ossuary.'), N_('The dungeon sealed with you still inside the Vaults.')],
  DISCONNECT: [N_('You lost connection on floor {n}.'), N_('You lost connection inside the Cellars.'), N_('You lost connection inside the Ossuary.'), N_('You lost connection inside the Vaults.')],
  ABANDON: [N_('You walked away from floor {n}.'), N_('You walked away from the Cellars.'), N_('You walked away from the Ossuary.'), N_('You walked away from the Vaults.')],
  DIED: [N_('You died on floor {n}.'), N_('You died in the Cellars.'), N_('You died in the Ossuary.'), N_('You died in the Vaults.')],
};

/**
 * The one line the player reads first. Phrased as a sentence rather than a
 * label because "Killed by: RATTLER" is a database row and "Killed by Rattler
 * in the Cellars" is an account of what happened.
 */
export function causeSentence(info: RunEndInfo): string {
  const kind: CauseKind = info.outcome === 'EXTRACTED' ? 'EXTRACTED' : (info.cause?.kind ?? 'DIED');
  const floor = info.stratum >= 1 && info.stratum <= 3 ? info.stratum : 0;
  const key = CAUSE_SENTENCE[kind][floor];
  const vars: Record<string, string | number> = { n: info.stratum };
  if (kind === 'MONSTER' && info.cause?.kind === 'MONSTER') {
    const english = MONSTER_DISPLAY_NAME[info.cause.ref as MonsterArchetype];
    vars.monster = english ? t(english) : info.cause.ref;
  }
  if (kind === 'PLAYER' && info.cause?.kind === 'PLAYER') vars.player = info.cause.ref;
  return t(key, vars);
}

/** Total catalogue value of a bag — the server sends each item's `baseValue`. */
export function totalValue(items: readonly RunEndItem[]): number {
  return items.reduce((sum, item) => sum + (item.value || ITEM_DEFS[item.itemDefId]?.baseValue || 0), 0);
}

/**
 * What that bag is actually worth in Wax (2026-09-02).
 *
 * This heading used to read `300g` — a gold unit the economy does not have,
 * and a number that was the *catalogue* value rather than anything a player
 * receives. Attaching the Wax coin to it would have promised exactly double
 * what the vendor pays, so the number moved to the sell price instead: the
 * same `sellPrice()` the Stash's own buttons use, so "what the run was worth"
 * and "what I get for it" are finally the same figure.
 */
export function totalSellValue(items: readonly RunEndItem[]): number {
  return items.reduce((sum, item) => sum + sellPrice({ baseValue: item.value || ITEM_DEFS[item.itemDefId]?.baseValue || 0 }), 0);
}

/**
 * The two run-end plates (2026-09-05).
 *
 * User: *"'You are down' game over screen — create a generated image and
 * design the screen shown when the game is lost."* Then: *"now do the
 * extraction plate too."*
 *
 * One picture per outcome, 960x530, and they are deliberately the same
 * corridor read two ways.
 *
 * **Defeat** is the corridor after you: a dropped helmet, a fallen sword,
 * coins nobody will collect, and a candle knocked over in a pool of its own
 * wax. **Extraction** is the way out: the door open, a lit corridor beyond it,
 * a wedge of light across the floor — and the same candle, upright and
 * burning. One object, two fates, which is the whole of what this game sells.
 *
 * Neither has a body or a character in it. The delver who died was the
 * player's, of whatever class they built, and drawing someone else's would
 * take that back.
 *
 * They come from different pipelines, which is worth knowing before editing
 * either. Defeat was **generated**, style-locked against this project's own
 * lobby tiles. Extraction is **rendered from the dungeon pack** by
 * `tools/render-run-end-plate.py` — the same GLTFs and the same atlas the
 * player walks past underground, which is the rule `render-item-icons.py` and
 * `render/stage-set.ts` already wrote down twice. The second one is the better
 * pipeline and defeat should be redone that way if it is ever touched.
 *
 * Both are fetched when this overlay is constructed, at the *start* of a run:
 * the moment either is needed is the worst possible moment to begin a
 * download, and 50KB across the pair is not worth being clever about.
 */
const PLATE_ART: Record<'DIED' | 'EXTRACTED', string> = {
  DIED: '/assets/ui/run-end/defeat.webp',
  EXTRACTED: '/assets/ui/run-end/extraction.webp',
};

export class RunEndOverlay {
  private readonly root: HTMLDivElement;
  private readonly card: HTMLDivElement;
  /** The art head: one plate per outcome, both mounted, one shown. */
  private readonly head: HTMLDivElement;
  /** Where the title sits when the art is up — over the picture's own bottom edge, not above it. */
  private readonly headTitleSlot: HTMLDivElement;
  private readonly plates: Record<'DIED' | 'EXTRACTED', HTMLImageElement>;
  /** Which plates have failed to load. A missing file costs its own outcome the picture and nothing else. */
  private readonly brokenPlates = new Set<string>();
  private readonly titleEl: HTMLDivElement;
  private readonly causeEl: HTMLDivElement;
  private readonly statsEl: HTMLDivElement;
  /** Holds the "Level up / Level 9" pair, empty on a run that crossed nothing. */
  private readonly levelEl: HTMLDivElement;
  private readonly lostEl: HTMLDivElement;
  private readonly keptEl: HTMLDivElement;
  private readonly primaryBtn: HTMLButtonElement;
  private readonly secondaryBtn: HTMLButtonElement;
  private visible = false;

  /** Set by the caller. Defaults navigate to `/play` rather than doing nothing — a dead end is the bug this screen exists to fix. */
  onReturnToHub: () => void = () => { window.location.href = '/play'; };
  onQueueAgain: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    // `items-start` + `my-auto` on the card rather than `items-center`: a
    // centred flex item taller than its scroll container has its own top
    // clipped and unreachable, which on a phone in landscape hid the verdict
    // itself. This centres when there is room and scrolls from the top when
    // there is not.
    this.root.className = 'fixed inset-0 z-[1200] hidden items-start justify-center '
      + 'scrim px-4 py-6 overflow-y-auto select-none';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', t('Run summary'));

    this.card = document.createElement('div');
    // `overflow-hidden` so the art head can bleed to the plaque's own rounded
    // edges instead of sitting inside a margin of stone.
    this.card.className = 'w-full max-w-[30rem] my-auto frame-raised overflow-hidden '
      + 'px-5 py-5 flex flex-col gap-4 text-center';

    // ─── The art head ───────────────────────────────────────────────
    // Negative margins exactly cancel the card's own px-5/pt-5: full bleed,
    // without a second element that has to be kept in sync with the padding.
    this.head = document.createElement('div');
    this.head.className = 'relative -mx-5 -mt-5 mb-1 hidden';

    const makePlate = (outcome: 'DIED' | 'EXTRACTED'): HTMLImageElement => {
      const img = document.createElement('img');
      img.src = PLATE_ART[outcome];
      // Decorative: the cause sentence below already says what happened, in
      // words, and a screen reader reading out a description of the floor would
      // only delay it.
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.width = 960;
      img.height = 530;
      img.decoding = 'async';
      img.draggable = false;
      // `max-h` earns its keep in landscape on a phone, where the natural
      // height of a 1.8:1 plate is most of the viewport and would push the
      // buttons off the bottom. The crop is biased low because both plates put
      // their subject on the floor, in the lower half of the frame.
      img.className = 'w-full aspect-[960/530] max-h-[34vh] sm:max-h-[46vh] '
        + 'object-cover object-[50%_62%] select-none animate-flicker hidden';
      // A missing file must cost the screen its picture and nothing else: the
      // title goes back where it was and the plaque reads exactly as it did
      // before there was any art at all.
      img.addEventListener('error', () => {
        this.brokenPlates.add(PLATE_ART[outcome]);
        if (!img.classList.contains('hidden')) {
          this.head.classList.add('hidden');
          this.card.insertBefore(this.titleEl, this.causeEl);
        }
      });
      return img;
    };
    this.plates = { DIED: makePlate('DIED'), EXTRACTED: makePlate('EXTRACTED') };

    // The lobby tiles' own gradient — text over generated art is a solved
    // problem in this codebase, and solving it twice is how two screens end up
    // disagreeing about how dark a caption needs its ground to be.
    const wash = document.createElement('div');
    wash.className = 'absolute inset-0 tile-scrim';

    // And a shorter one from the top, so the lit wall panels at the frame's
    // upper corners settle into the plaque instead of ending at its edge. The
    // art should look like it is *inside* the stone, not printed on it.
    const lid = document.createElement('div');
    lid.className = 'absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/60 to-transparent';

    this.headTitleSlot = document.createElement('div');
    this.headTitleSlot.className = 'absolute inset-x-0 bottom-0 px-4 pb-3';

    this.head.append(this.plates.DIED, this.plates.EXTRACTED, wash, lid, this.headTitleSlot);

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'font-heading font-black text-[clamp(2.2rem,8vw,3.6rem)] leading-none';
    // Set unconditionally rather than only when the title is over the art: on
    // the plain plaque it is invisible, and a style that moves with the element
    // is one fewer thing to remember when it moves.
    this.titleEl.style.textShadow = '0 2px 16px rgba(0,0,0,0.95), 0 0 32px rgba(0,0,0,0.75)';

    this.causeEl = document.createElement('div');
    this.causeEl.className = 'text-text-dim text-[clamp(0.9rem,2.4vw,1.05rem)] leading-snug';

    this.statsEl = document.createElement('div');
    // Ruled top and bottom: three figures with nothing around them read as a
    // caption, and this row is the run's ledger line.
    this.statsEl.className = 'flex justify-center gap-6 flex-wrap eyebrow eyebrow-dim '
      + 'border-y border-[#4a3c2d]/70 py-2';

    // `empty:hidden` on all three: a run that carried nothing and crossed no
    // level leaves them with no children at all, and three empty flex items
    // inside a `gap-4` column push the buttons down by sixty pixels of nothing.
    this.levelEl = document.createElement('div');
    this.levelEl.className = 'empty:hidden';

    this.lostEl = document.createElement('div');
    this.lostEl.className = 'empty:hidden';
    this.keptEl = document.createElement('div');
    this.keptEl.className = 'empty:hidden';

    const buttons = document.createElement('div');
    // Stacked on a phone, side by side once there is room — these are the only
    // interactive elements in the game's most important modal, so they get the
    // full 44px tap target and then some.
    buttons.className = 'flex flex-col sm:flex-row gap-2 pt-1';

    this.primaryBtn = document.createElement('button');
    this.primaryBtn.type = 'button';
    this.primaryBtn.className = 'btn btn-gold flex-[1.6]';
    this.primaryBtn.addEventListener('click', () => { this.hide(); this.onReturnToHub(); });

    this.secondaryBtn = document.createElement('button');
    this.secondaryBtn.type = 'button';
    this.secondaryBtn.className = 'btn btn-stone flex-1 hidden';
    this.secondaryBtn.textContent = t('Queue Again');
    this.secondaryBtn.addEventListener('click', () => { this.hide(); this.onQueueAgain?.(); });

    buttons.append(this.primaryBtn, this.secondaryBtn);
    this.card.append(this.head, this.titleEl, this.causeEl, this.statsEl, this.levelEl, this.lostEl, this.keptEl, buttons);
    this.root.appendChild(this.card);
    container.appendChild(this.root);
  }

  show(info: RunEndInfo): void {
    const died = info.outcome === 'DIED';
    const lost = info.lost ?? [];
    const kept = info.kept ?? [];

    this.titleEl.textContent = died ? t('You died') : t('Extracted');
    this.titleEl.style.color = died ? 'var(--color-error)' : 'var(--color-success)';
    this.causeEl.textContent = causeSentence(info);

    // The verdict goes *on* the picture, not above it — the words and the
    // corridor they describe are one image, and a title floating over a
    // separate photograph is a magazine layout, not a game over screen.
    const outcome = died ? 'DIED' : 'EXTRACTED';
    const useArt = !this.brokenPlates.has(PLATE_ART[outcome]);
    this.plates.DIED.classList.toggle('hidden', !(useArt && died));
    this.plates.EXTRACTED.classList.toggle('hidden', !(useArt && !died));
    this.head.classList.toggle('hidden', !useArt);
    if (useArt) this.headTitleSlot.appendChild(this.titleEl);
    else this.card.insertBefore(this.titleEl, this.causeEl);

    this.statsEl.replaceChildren(
      stat(t('{n} min', {
        n: (info.durationMs / 60_000).toLocaleString(getIntlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      }), t('survived')),
      stat(`+${(info.xpGained ?? 0).toLocaleString(getIntlLocale())}`, t('XP')),
      stat(String(kept.length + lost.length), t('carried')),
    );

    // A level earned by getting out (or by dying far enough in) — the one
    // crossing that has no in-world moment to attach to, since by the time it
    // is written the world is gone. Highlighted rather than folded into the XP
    // number, which is a figure you skim.
    this.levelEl.replaceChildren();
    if (info.leveledTo) {
      const line = document.createElement('div');
      line.className = 'mt-3 text-center text-amber-100 font-semibold text-[clamp(1rem,3vw,1.35rem)]';
      line.style.textShadow = '0 0 12px rgba(255,214,120,0.8)';
      line.textContent = t('Level {n}', { n: info.leveledTo });
      const sub = document.createElement('div');
      sub.className = 'text-center font-heading italic text-[1rem] text-amber-200/70';
      sub.textContent = t('Level up');
      this.levelEl.append(sub, line);
    }

    // Losses first, and only when there were any. An empty "Lost" heading on a
    // run where you carried nothing is a reproach for something that did not
    // happen.
    this.lostEl.replaceChildren();
    if (lost.length > 0) {
      this.lostEl.append(itemList(t('Lost'), lost, 'text-red-400', 'line-through decoration-red-400/60'));
    }

    this.keptEl.replaceChildren();
    if (kept.length > 0) {
      const heading = died ? t('Saved by insurance') : t('Extracted');
      this.keptEl.append(itemList(heading, kept, 'text-success', ''));
    } else if (died && lost.length > 0) {
      // The teaching moment: the player just lost a bag and owns zero
      // insurance slots. Said once, here, where it costs them something.
      const hint = document.createElement('div');
      hint.className = 'text-[0.75rem] text-amber-200/80 leading-snug';
      hint.textContent = (info.insuranceSlots ?? 0) > 0
        ? t('None of it was insured. Mark items in your pack before the next run.')
        : t('Insurance slots would have saved some of this.');
      this.keptEl.append(hint);
    }

    // The satchel line, after the loot lists — a charge is worth a fraction of
    // an item, so it belongs below them, and only when there was something in
    // the bag to speak of.
    const charges = (info.satchel ?? []).reduce((sum, c) => sum + c.charges, 0);
    if (charges > 0) {
      const line = document.createElement('div');
      line.className = `text-[0.75rem] leading-snug ${info.satchelReturned ? 'text-text-dim' : 'text-red-400'}`;
      line.textContent = info.satchelReturned
        ? tp(charges, '{n} unused charge back in your satchel.', '{n} unused charges back in your satchel.')
        : tp(charges, '{n} unused charge lost with the bag.', '{n} unused charges lost with the bag.');
      this.keptEl.append(line);
    }

    // The Rite line, last, because it is the one thing on this screen the
    // player chose before the run rather than during it — it reads as the
    // verdict on a decision, which is what it is.
    if (info.rite) {
      const { name, bonusXp, lapsedReason } = info.rite;
      const line = document.createElement('div');
      line.className = lapsedReason === null
        ? 'text-[0.75rem] leading-snug text-gold'
        : 'text-[0.75rem] leading-snug text-red-400';
      line.textContent = lapsedReason === null
        ? t('{rite} — +{xp} XP', { rite: t(name), xp: bonusXp })
        : riteLapseLine(t(name), lapsedReason);
      this.keptEl.append(line);
    }

    this.primaryBtn.textContent = t('Return to the Sconce');
    this.secondaryBtn.classList.toggle('hidden', this.onQueueAgain === null);

    this.root.classList.remove('hidden');
    this.root.classList.add('flex');
    // Restart the entrance on every show: a player who dies twice in five
    // minutes should get the same beat the second time, and an animation that
    // has already run does not replay just because its element reappeared.
    this.card.classList.remove('animate-verdict');
    void this.card.offsetWidth;
    this.card.classList.add('animate-verdict');
    this.visible = true;
    // Focus the way out, so a keyboard or a screen reader lands on it rather
    // than on whatever the canvas had.
    this.primaryBtn.focus();
  }

  /** Whether the summary is currently on screen — the caller uses this to know the player has not finished with it yet. */
  get isShowing(): boolean {
    return this.visible;
  }

  hide(): void {
    this.root.classList.add('hidden');
    this.root.classList.remove('flex');
    this.visible = false;
  }

  dispose(): void {
    this.root.remove();
  }
}

function stat(value: string, label: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center';
  const v = document.createElement('span');
  v.className = 'text-base font-semibold text-text normal-case tracking-normal';
  v.textContent = value;
  const l = document.createElement('span');
  l.textContent = label;
  el.append(v, l);
  return el;
}

function itemList(heading: string, items: readonly RunEndItem[], tint: string, rowExtra: string): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'text-left frame-inset px-3 py-2 flex flex-col gap-1';

  const head = document.createElement('div');
  head.className = `font-heading font-bold text-[0.85rem] ${tint} flex justify-between`;
  const headLabel = document.createElement('span');
  headLabel.textContent = heading;
  const headValue = document.createElement('span');
  // The total is the point: one number for what the run was worth, so the
  // player does not have to add up a list to know how the gamble went.
  headValue.innerHTML = amountMarkup('wax', totalSellValue(items), { hideLabel: true });
  head.append(headLabel, headValue);
  wrap.appendChild(head);

  // Capped, with the remainder counted rather than silently dropped — a bag of
  // twenty would otherwise push the buttons off a phone screen.
  const MAX_ROWS = 6;
  for (const item of items.slice(0, MAX_ROWS)) {
    const row = document.createElement('div');
    row.className = `flex justify-between text-[0.8rem] ${rowExtra}`;
    const name = document.createElement('span');
    name.className = item.rarity === 'RARE' ? 'text-gold-bright' : 'text-text';
    name.textContent = t(itemLabel(item.itemDefId));
    const value = document.createElement('span');
    value.className = 'text-text-dim tabular-nums';
    // Per-row prices moved off the phantom `g` unit for the same reason the
    // header total did: this is what the vendor pays for that item, so it
    // adds up to the total above it and matches the Stash's own sell button.
    value.innerHTML = amountMarkup('wax', sellPrice({ baseValue: item.value }), { hideLabel: true, tone: 'text-text-dim' });
    row.append(name, value);
    wrap.appendChild(row);
  }
  if (items.length > MAX_ROWS) {
    const more = document.createElement('div');
    more.className = 'text-[0.75rem] text-text-dim';
    more.textContent = tp(items.length - MAX_ROWS, 'and {n} more', 'and {n} more');
    wrap.appendChild(more);
  }
  return wrap;
}
